import React, { useState, useEffect } from 'react';
import { X, Eye, Loader2, Search, Calendar } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import { HistoryDetailsModal } from './HistoryDetailsModal';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import * as API from '../../../api';

type HistoryModalProps = {
  onClose: () => void;
  year: string;
};

type HistoryEntry = {
  id: string;
  name: string;
  email: string;
  created_at: string;
  data: string;
};

export const HistoryModal = ({ onClose, year }: HistoryModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<HistoryEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    filterHistory();
  }, [searchTerm, dateRange, history]);

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const response = await API.getDataTransferMetaFinanceiraHistory();
      response.data.data.forEach((item:any) => {
        item.data = JSON.parse(item.data);
      })
      console.log(response.data.data);
      response.data.data = response.data.data.filter((item:any) => {
        return item.data.selectedYear == year
      });
      setHistory(response.data.data);
      setFilteredHistory(response.data.data);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterHistory = () => {
    let filtered = [...history];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        entry.name.toLowerCase().includes(searchLower) ||
        entry.email.toLowerCase().includes(searchLower)
      );
    }

    // Apply date range filter
    if (startDate && endDate) {
        // Garante que o startDate pegue o primeiro horário do dia
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
      
        // Garante que o endDate pegue o último horário do dia
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
      
        filtered = filtered.filter(entry => {
          const entryDate = new Date(entry.created_at);
          return entryDate >= start && entryDate <= end;
        });
      }
      

    setFilteredHistory(filtered);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">
          Histórico de Alterações - {year}
        </h3>
        <button
          onClick={onClose}
          className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Date Range Picker */}
          <div className="flex items-center gap-2">
            <Calendar className="text-gray-400 w-5 h-5 flex-shrink-0" />
            <DatePicker
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              placeholderText="Filtrar por período..."
              className="w-full w-250px px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500"
              dateFormat="dd/MM/yyyy"
              isClearable
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="mt-4 text-gray-500">Carregando histórico...</p>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-gray-500">
              {searchTerm || (startDate && endDate) 
                ? 'Nenhum resultado encontrado para os filtros aplicados'
                : 'Nenhuma alteração encontrada'}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredHistory.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => setSelectedEntry(entry)}
                      className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Ver detalhes"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Details Modal */}
      <Modal
        show={selectedEntry !== null}
        onHide={() => setSelectedEntry(null)}
        dialogClassName="mw-1300px"
        centered
      >
        {selectedEntry && (
          <HistoryDetailsModal
            data={selectedEntry}
            onClose={() => setSelectedEntry(null)}
          />
        )}
      </Modal>
    </div>
  );
};
